import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { createSlug, normalizeSlug } from '../utils/slugify';
import { truncateHtmlIntoString } from '../utils/truncateHtmlIntoString';
import { getDisplayPrice, getOrigPrice } from '../utils/amountHelper';
import { formatCurrency } from '../utils/formatCurrency';
import { useCartActions } from '../hooks/useCartActions';
import { CatalogDispatchContext, CartContext } from '../store/CatalogContext';
import type { Product, ProductState, UpsellItem } from '../types/product';
import type { CartProduct } from '../types/cart';

interface ProductCardProps {
  product: Product | UpsellItem | null;
}

export default function ProductCard({ product }: ProductCardProps) {
  const rawSlug = product?.urlSlug || createSlug(product?.name || '');
  const urlSlug = normalizeSlug(rawSlug);

  const catalogDispatch = useContext(CatalogDispatchContext);
  const cart = useContext(CartContext);

  const [productState, setProductState] = useState<ProductState>({
    productId: product?.id || null,
    productInCart: null,
    quantity: 1,
    // upsells: product && 'upsellProducts' in product ? product.upsellProducts ?? [] : [],
    // selectedUpsellProductIds: [],
    isUpdatingCart: false,
    // isUpsellUpdated: false,
    // isQuantityUpdated: false,
  });

  useEffect(() => {
    const thisProductInCart = cart?.cartProducts?.find((item: CartProduct) => item.productId === product?.id);

    setProductState((prevState) => ({
      ...prevState,
      productInCart: typeof thisProductInCart === 'undefined' ? null : thisProductInCart,
    }));
  }, [product, cart]);

  const { addToCart } = useCartActions({
    setState: setProductState,
    catalogDispatch,
  });

  const handleCartUpdate = () => {
    // if product is already in cart, show cart widget
    if (productState?.productInCart) {
      catalogDispatch({
        type: 'UX',
        payload: { showCartWidget: true },
      });
      return;
    }

    if (!productState?.productInCart) {
      addToCart(productState);
      return;
    }
  };

  return (
    <div className="product-col mb-3 mb-md-3 mb-xl-4">
      <div
        className={`card product-card h-100 position-relative pt-4 ${productState.isUpdatingCart ? 'adding-to-cart' : ''} ${
          product && 'bundleContents' in product && product.bundleContents ? 'bundle' : ''
        }`}
        data-pid={product?.id}
      >
        <div className="price-display position-absolute rounded fw-600">
          {product?.pricing && product.pricing.combinedDiscount > 0 && product.pricing.discountSource !== 'Upsell' && (
            <span className="orig-price">{product && formatCurrency(getOrigPrice(product))}</span>
          )}
          <span className="price">{product && formatCurrency(getDisplayPrice(product))}</span>
        </div>
        <div className="card-body">
          <h3 className="fs-5 card-title text-theme-primary">
            <Link to={`/product/${urlSlug}/?id=${product?.id}`}>{product?.name}</Link>
          </h3>
          <div className="description-wrap">{truncateHtmlIntoString(product?.description || '')}</div>
        </div>
        <div className="card-footer bg-transparent border-0 pb-3 d-flex justify-content-between align-items-center">
          <button
            className={`add-to-cart-btn btn btn-outline-success fw-600 ${productState.productInCart ? 'view-cart' : ''}`}
            onClick={handleCartUpdate}
          >
            {productState.productInCart ? 'View Cart' : 'Add to Cart'}
          </button>
          <Link className="d-block text-muted me-xxl-2" to={`/product/${urlSlug}/?id=${product?.id}`}>
            &raquo; View Details
          </Link>
        </div>
      </div>
    </div>
  );
}
